import axios, {
  AxiosError,
  AxiosInstance,
  type InternalAxiosRequestConfig,
  AxiosHeaders,
} from "axios";
import { API_BASE_URL, API_ENDPOINTS } from "./apiConfig";

const AUTH_REDIRECT_MESSAGE_KEY = "authRedirectMessage";
const AUTH_REDIRECT_MESSAGE_TEXT = "로그아웃 되었습니다.";

const baseAxios = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false,
});

const TOKEN_KEYS = {
  access: "authorization",
  refresh: "refresh-token",
} as const;

export const getAccessToken = () => localStorage.getItem(TOKEN_KEYS.access) || "";
export const getRefreshToken = () => localStorage.getItem(TOKEN_KEYS.refresh) || "";

export const setTokens = (access?: string, refresh?: string) => {
  if (access) localStorage.setItem(TOKEN_KEYS.access, access);
  if (refresh) localStorage.setItem(TOKEN_KEYS.refresh, refresh);
};
export const clearTokens = () => {
  localStorage.removeItem(TOKEN_KEYS.access);
  localStorage.removeItem(TOKEN_KEYS.refresh);
};

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false,
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const access = getAccessToken();

  const headers = (config.headers ||= new AxiosHeaders());

  if (access) {
    headers.set("Authorization", access);
  }
  headers.set("site-type", "ADMIN");

  return config;
});

let isRefreshing = false;
type Waiter = { resolve: (token: string) => void; reject: (err: any) => void };
let waiters: Waiter[] = [];

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig | undefined;
    const status = error.response?.status;

    if (!original || status !== 401) {
      return Promise.reject(error);
    }

    const normalizeHeaders = (h: InternalAxiosRequestConfig["headers"]) =>
      h instanceof AxiosHeaders ? h : new AxiosHeaders(h as any);

    if (isRefreshing) {
      try {
        const newAccess = await new Promise<string>((resolve, reject) => {
          waiters.push({ resolve, reject });
        });
        const h = normalizeHeaders(original.headers);
        h.set("Authorization", newAccess);
        original.headers = h;
        return api(original);
      } catch (e) {
        return Promise.reject(e);
      }
    }

    isRefreshing = true;
    try {
      const refresh = getRefreshToken();
      if (!refresh) {
        clearTokens();
        throw error;
      }

      const resp = await baseAxios.get(API_ENDPOINTS.refresh, {
        headers: {
          "refresh-token": getRefreshToken(), 
          "site-type": "ADMIN",           
        },
      });

      const headers = resp.headers as Record<string, string | undefined>;
      const newAccess = headers["authorization"] || headers["Authorization"];
      const newRefresh = headers["refresh-token"] || headers["Refresh-Token"];

      if (!newAccess) {
        clearTokens();
        throw error;
      }

      setTokens(String(newAccess), newRefresh ? String(newRefresh) : undefined);

      waiters.forEach(w => w.resolve(String(newAccess)));
      waiters = [];

      const h = normalizeHeaders(original.headers);
      h.set("Authorization", String(newAccess));
      original.headers = h;
      return api(original);
    } catch (e) {
      waiters.forEach(w => w.reject(e));
      waiters = [];
      clearTokens();
      return Promise.reject(e);
    } finally {
      isRefreshing = false;
    }
  }
);

function forceLoginRedirectWithMessage(msg: string) {
  try {
    if (typeof clearTokens === "function") clearTokens();
  } catch {}
  try {
    sessionStorage.setItem(AUTH_REDIRECT_MESSAGE_KEY, msg);
  } catch {}
  if (window.location.pathname !== "/login") {
    window.location.replace("/login");
  }
}

api.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error?.response?.status;
    if (status === 401 || status === 403) {
      forceLoginRedirectWithMessage(AUTH_REDIRECT_MESSAGE_TEXT);
    }
    return Promise.reject(error);
  }
);



export default api;